import { useContext } from 'react';
import { useQuery } from 'react-query';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import useAxios from './useAxios';

export enum OnlyFansMetricUserSalesDetailsRiskLevel {
    NONE = 0,
    LOW = 1, // Within 50% of expected gap
    MODERATE = 2, // Within 20% of expected gap
    HIGH = 3, // Within 10% of expected gap
    VERY_HIGH = 4, // Within 5% of expected gap
    EXTREME = 5, // Within 1% of expected gap (almost certainly bots)
}

export interface OnlyFansMetricUserSalesDetails {
    _id: string;
    userId: number;
    name: string;
    userName: string;
    riskLevel: OnlyFansMetricUserSalesDetailsRiskLevel;
    subscribedAt: Date;
    createdAt: Date;
    firstSaleDate: Date | null;
    lastSaleDate: Date | null;
    total: number | object;
    totalWithoutSubscriptions: number | object;
    totalMessage: number | object;
    totalResubscription: number | object;
    totalTip: number | object;
    totalSubscription: number | object;
    totalPost: number | object;
    totalStream: number | object;
    totalChargebacks: number | object;
    totalChargebacksWithoutSubscriptions: number | object;
    totalChargebacksMessage: number | object;
    totalChargebacksResubscription: number | object;
    totalChargebacksTip: number | object;
    totalChargebacksSubscription: number | object;
    totalChargebacksPost: number | object;
    totalChargebacksStream: number | object;
}

const useSextforceMetricsUsersSalesDetails = (
    subscriberId: string | undefined | null,
    metricId: string | undefined | null,
    metricType: string | undefined | null,
    startDate?: Date | null,
    endDate?: Date | null,
    staggerPreviousStepReady: boolean = true,
) => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const axios = useAxios();

    const fetchData = async (): Promise<OnlyFansMetricUserSalesDetails[]> => {
        if (!subscriberId) {
            return [];
        }

        if (!metricId) {
            return [];
        }

        if (!metricType) {
            return [];
        }

        const query: string = `${settingsContext.routes.metrics.base}${subscriberId}/${
            metricType === 'promoCampaign' ? 'promocampaigns' : 'trials'
        }/${metricId}/sales?${new URLSearchParams({
            ...(startDate ? { startDate: startDate.toISOString() } : {}),
            ...(endDate ? { endDate: endDate.toISOString() } : {}),
        })}`;

        const data = await axios
            .get(query, {
                // timeout 20 minutes
                timeout: 20 * 60 * 1000,
            })
            .then(response => response.data)
            .catch(error => {
                console.error(error);

                return [];
            });

        return data;
    };

    return useQuery(['metricsPromoCampaingSales', subscriberId, metricId, startDate, endDate], () => fetchData(), {
        refetchOnWindowFocus: false,
        // 5 minutes
        staleTime: 60 * 1000 * 5,
        enabled: Boolean(
            userContext.jwtToken && settingsContext.apiKey && subscriberId && metricId && metricType && staggerPreviousStepReady,
        ),
    });
};

export default useSextforceMetricsUsersSalesDetails;
